/* eslint-disable */
import React, { useMemo } from "react";
import {
  Card,
  Group,
  Text,
  Title,
  Box,
  useMantineTheme,
  Loader,
  Center,
} from "@mantine/core";

import * as Recharts from "recharts";
import { useGetAttendanceSummaryRange } from "api/attendanceReport/getyearlyReport";

const ResponsiveContainerAny =
  Recharts.ResponsiveContainer as unknown as React.ComponentType<any>;
const LineChartAny = Recharts.LineChart as unknown as React.ComponentType<any>;
const LineAny = Recharts.Line as unknown as React.ComponentType<any>;
const XAxisAny = Recharts.XAxis as unknown as React.ComponentType<any>;
const YAxisAny = Recharts.YAxis as unknown as React.ComponentType<any>;
const CartesianGridAny =
  Recharts.CartesianGrid as unknown as React.ComponentType<any>;
const TooltipAny = Recharts.Tooltip as unknown as React.ComponentType<any>;

const isoDate = (d: Date | null) => {
  if (!d) return undefined;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface AttendanceChartProps {
  dateRange: [Date | null, Date | null];
}

export default function AttendanceChart({ dateRange }: AttendanceChartProps) {
  const theme = useMantineTheme();

  const startDate = isoDate(dateRange[0]);
  const endDate = isoDate(dateRange[1]);

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useGetAttendanceSummaryRange(startDate, endDate);

  const chartData: Array<{
    month: string;
    present?: number;
    absent?: number;
  }> = useMemo(() => {
    if (!apiResponse) return [];
    if (Array.isArray((apiResponse as any).data))
      return (apiResponse as any).data;
    if (Array.isArray(apiResponse)) return apiResponse as any;
    return [];
  }, [apiResponse]);

  const yMax = useMemo(() => {
    const maxVal = chartData.reduce(
      (acc, cur) => Math.max(acc, cur.present ?? 0, cur.absent ?? 0),
      0
    );
    if (maxVal <= 10) return 10;
    return Math.ceil((maxVal + 1) / 10) * 10;
  }, [chartData]);

  return (
    <Card radius="md" shadow="xs" style={{ height: "100%" }}>
      <Group position="apart" align="flex-start" style={{ marginBottom: 6 }}>
        <Group spacing={8} align="center">
          <svg
            width="22"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 18h18"
              stroke="#E74C3C"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M7 13l3-3 4 4 7-7"
              stroke="#E74C3C"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Title order={4} style={{ margin: 0 }}>
            Attendance
          </Title>
        </Group>

        <Group spacing={8} align="center">
          <Text size="xs" color="dimmed">
            {startDate && endDate ? `${startDate} – ${endDate}` : "Select a date range"}
          </Text>
        </Group>
      </Group>

      <Box style={{ width: "100%", height: 230 }}>
        {isLoading ? (
          <Center style={{ height: "100%" }}>
            <Loader />
          </Center>
        ) : error ? (
          <Center style={{ height: "100%" }}>
            <Text color="red">Failed to load attendance</Text>
          </Center>
        ) : (
          <ResponsiveContainerAny width="100%" height="100%">
            <LineChartAny
              data={chartData}
              margin={{ top: 10, right: 20, left: 5, bottom: 10 }}
            >
              <defs>
                <linearGradient id="gradPresent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2FA84F" stopOpacity={0.14} />
                  <stop offset="100%" stopColor="#2FA84F" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradAbsent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E74C3C" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#E74C3C" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGridAny
                vertical={false}
                strokeDasharray="0"
                stroke={theme.colorScheme === "dark" ? "#2b2b2b" : "#f0f0f0"}
              />

              <XAxisAny
                dataKey="month"
                axisLine={false}
                tickLine={false}
                padding={{ left: 20, right: 20 }}
                tick={{ fill: "#222", fontSize: 14 }}
                label={{
                  value: "Month",
                  position: "insideBottom",
                  dy: 12,
                  style: {
                    fill: theme.colorScheme === "dark" ? "#fff" : "#222",
                    fontSize: 13,
                    fontWeight: 600,
                  },
                }}
              />

              <YAxisAny
                domain={[0, yMax]}
                axisLine={false}
                tickLine={false}
                ticks={[
                  Math.round(yMax / 5),
                  Math.round((yMax / 5) * 2),
                  Math.round((yMax / 5) * 3),
                  Math.round((yMax / 5) * 4),
                  yMax,
                ]}
                tick={{ fill: "#222", fontSize: 12 }}
                label={{
                  value: "Count", // Y axis heading (change text to whatever you want)
                  angle: -90, // rotates label vertically
                  position: "insideLeft", // places it inside left area
                  offset: 0,
                  style: {
                    fill: theme.colorScheme === "dark" ? "#fff" : "#666",
                    fontSize: 13,
                    fontWeight: 600,
                  },
                }}
              />

              <TooltipAny
                contentStyle={{
                  borderRadius: 8,
                  border: "none",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                }}
              />

              <LineAny
                type="monotone"
                dataKey="present"
                stroke="#28A745"
                strokeWidth={6}
                dot={false}
                activeDot={{ r: 6 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                isAnimationActive={false}
              />

              <LineAny
                type="monotone"
                dataKey="absent"
                stroke="#E74C3C"
                strokeWidth={6}
                dot={false}
                activeDot={{ r: 6 }}
                strokeLinecap="round"
                strokeLinejoin="round"
                isAnimationActive={false}
              />
            </LineChartAny>
          </ResponsiveContainerAny>
        )}
      </Box>

      <Group position="center" spacing={20} style={{ marginTop: 10 }}>
        <Group spacing={8} align="center">
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 14,
              background: "#28A745",
              display: "inline-block",
              boxShadow: "0 2px 0 rgba(0,0,0,0.06)",
            }}
          />
          <Text size="sm">Present</Text>
        </Group>

        <Group spacing={8} align="center">
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 14,
              background: "#E74C3C",
              display: "inline-block",
              boxShadow: "0 2px 0 rgba(0,0,0,0.06)",
            }}
          />
          <Text size="sm">Absent</Text>
        </Group>
      </Group>
    </Card>
  );
}
